import { Nugget } from '@deltek/specification-client/core/Models/nugget'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectNugget } from '@deltek/specification-client/core/Models/projectNugget'
import { ProjectUniformatClassification } from '@deltek/specification-client/core/Models/projectUniformatClassification'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { UniformatClassification } from '@deltek/specification-client/core/Models/uniformatClassification'
import {
    ElementMetadata,
    GetNuggetResponse,
    GetProjectNuggetResponse,
    ImportedManufacturersAndBOD,
    NuggetWithSpecAgentId,
    ProjectElementNodeWithDrawingDesignation,
    ProjectManufacturerProductDetailOnFamily,
    ProjectNuggetWithSpecAgentId,
} from 'api/graphQL/types/projectElement.types'
import { FamilyIteration } from 'models/projectElementNode'
import { ProjectElementMetadata } from 'models/projectElements'
import { ProjectSettings } from 'models/projectSettings'
import {
    ElementTreeViews,
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES,
    PART_TYPES,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { ProjectElementForImport } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import { Key } from 'react'
import { ContentItemViewNodesType } from './contentItemViewNode.type'

export interface ProductFamilyView {
    id: string
    prefix: string
    familyId: string
    delimiter: string
    name: string
    nuggets: Partial<Nugget>[]
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
}

export interface GetProductFamilyViewResponse {
    productFamilyView: Partial<ProductFamilyView>
}

export interface ProjectProductFamilyViewWithPTP
    extends ProjectProductFamilyView {
    familyAddedProductTypes?: ProjectElementNodeWithDrawingDesignation[]
    familyProductListings?: Partial<ProjectManufacturerProductDetailOnFamily>[]
    familyCustomManufacturerProducts?: Partial<
        ProjectManufacturerProductDetailOnFamily
    >[]
    familyManufacturerImportedManufacturer?: Partial<
        ImportedManufacturersAndBOD
    >[]
    familyBodImportedManufacturer?: Partial<ImportedManufacturersAndBOD>[]
    metadata?: ElementMetadata
}

export interface GetProjectProductFamilyViewResponse {
    getProjectProductFamilyView: Partial<ProjectProductFamilyViewWithPTP>
}

export interface ProjectProductFamilyView {
    id: string
    prefix: string
    familyId: string
    delimiter: string
    name: string
    projectNuggets: Partial<ProjectNugget>[]
    projectElementMetadata: Partial<ProjectElementMetadata>
    hasProductType: boolean
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    firmId: string
    notesCount: number
}

export interface GetNuggetContentViewResponse extends GetNuggetResponse {
    nugget: Partial<NuggetWithSpecAgentId> & {
        contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    }
}

export interface GetProjectNuggetContentViewResponse
    extends GetProjectNuggetResponse {
    getProjectNugget: Partial<ProjectNuggetWithSpecAgentId> & {
        contentItemViewNodes: Partial<ContentItemViewNodesType>[]
        metadata: ElementMetadata
        projectElementMetadata?: Partial<ProjectElementMetadata>
    }
}

/**
 * Response type for SetProjectContentItemElements mutation
 * Used for UPDATE operations to avoid redundant GET calls
 */
export interface SetProjectContentItemElementsResponse {
    data: {
        setProjectContentItemElements: {
            contentItemViewNodes: ContentItemViewNodesType[]
        }
    }
}

export type NodeType = typeof NODE_TYPES[keyof typeof NODE_TYPES]

export type PartType = typeof PART_TYPES[keyof typeof PART_TYPES]

/**
 * Represents a node in the hierarchical tree data structure
 * TODO: Add more properties as needed to render the tree structure
 * */
export type DataNode<T = Partial<TextContentElement>> = {
    key: string
    type: NodeType
    contentItemNodes: T[]
    contentItemView?: Partial<ContentItemViewNodesType>
    order: number
    rootArticleId?: string
    children?: DataNode<T>[]
    specifierNotes?: string[]
    parentComments?: string[]
    expanded?: boolean
    isHidden?: boolean
    /** For synthetic product-library-btn nodes: carries the parent paragraph's
     * hidden state so CustomManufacturerFields can be disabled when the paragraph
     * is explicitly deactivated, without affecting the button node's own
     * tree-level visibility (Bug 2688614 / Bug 2684633). */
    parentIsHidden?: boolean
    isDisabled?: boolean
    isDeactivated?: boolean
    isLeaf?: boolean
    isProductGroup?: boolean
    isProductType?: boolean
    isUserAdded?: boolean
    isProductTypeContent?: boolean
    isManufacturers?: boolean
    isBOD?: boolean
    isCustomManufacturer?: boolean
    isBestPractice?: boolean
    isGlobalBestPractice?: boolean
    isAddedInOfficeMasterDraftProject?: boolean
    /** Set on synthetic `product-library-btn-*` rows: id of the owning MFR/BOD
     * paragraph DataNode. Used by CustomManufacturerFieldsWrapper to scope its
     * custom-mfr pill filter to this paragraph (Bug 2690689). */
    parentContentItemId?: string
}

export interface ElementItemNode {
    id: string
    actionFlag: string
    elementId: string
    elementType: string
    isProductTypeProject: boolean
    isProjectProductFamily: boolean
    baseElementId?: string
    /** the elementId used for update mutation (PT level) */
    parentFamilyId?: string
    /** The current family ID for PT cache lookups (family's elementId when family selected, or PT's parentFamilyId when PT selected) */
    currentFamilyId?: string
    /** Manufacturer firm name from service worker message */
    firmName?: string
    /** Product name from service worker message */
    productName?: string
}

export interface AssignedUserList {
    userName: string
    userId?: string | null
}

/** Represents the props for the workspace editor component */
export interface WorkspaceEditorProps {
    /** The current selected project element node from element tree */
    selectedProjectElementNode?: Partial<ProjectElementNode> | null
    /** The current project uniformat classification data */
    selectedElementData?:
        | Partial<ProjectUniformatClassification>
        | Partial<UniformatClassification>
    /** The projectId currently loaded in the workspace */
    projectId: string
    /** The flag to indicate if the element tree is currently loading */
    isElementTreeLoading?: boolean
    /** The flag to indicate if the product family is currently loading */
    isTrackChangeToggleOn?: boolean
    insertNewProductTypeElement?: (
        element: ProjectElementNode,
        isSelectedFromListingDialog?: boolean,
        newNodesForAdditionalInstances?: ProjectElementNode[]
    ) => void
}

/** Represents the global actions available for the workspace */
export interface Workspace2Actions {
    /** sets whether the content is from OM/SMM */
    setIsContentTeamFromStudioPage: (
        isContentTeamFromStudioPage: boolean
    ) => void
    /** sets the current project ID */
    setProjectId: (projectId: string) => void
    /** sets the tree data pre-processed in tree data mapper */
    setTreeData: (treeData: DataNode[]) => void
    /** sets whether text is currently being edited in PlainText */
    setIsEditingPlainText: (isEditing: boolean) => void
    /** deactivates all active editors by incrementing the deactivation signal */
    deactivateAllEditors: () => void
    /** sets the selected project element node from element tree */
    setSelectedProjectElementNode: (
        selectedProjectElementNode: Partial<ProjectElementNode>
    ) => void
    /** sets the active tab */
    setActiveTab: (activeTab: number) => void
    /** sets the articles loading state */
    setArticlesLoading: (keys: string[]) => void
    /** adds a node key to the in-flight deactivation set */
    addDeactivationPending: (nodeKey: string) => void
    /** removes a node key from the in-flight deactivation set */
    removeDeactivationPending: (nodeKey: string) => void
    /** sets the expanded keys */
    setExpandedKeys: (keys: React.Key[]) => void
    /** sets the selected project element nodes */
    setContentItemViewNodes: (
        contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    ) => void
    /** sets content item view nodes without bumping editorDeactivationSignal (use only for silent reconciliation) */
    setContentItemViewNodesSilently: (
        contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    ) => void
    /** sets the previous tree content item view nodes */
    setPreviousContentItemViewNodes: (
        previousContentItemViewNodes: Partial<ContentItemViewNodesType>[]
    ) => void
    /** sets the current project uniformat classification data */
    setClassificationNodes: (classificationNodes: DataNode[]) => void
    /** sets the currently being added text content element */
    setTextContentElementToAdd: (
        element: Partial<TextContentElement> | null
    ) => void
    /** switches the workspace between enabled and disabled states */
    switchWorkspace: (enabled: boolean) => void
    /** sets the maintenance mode and stores the associated error */
    setMaintenanceMode: (error: Error | null) => void
    /** sets the workspace content to the default state (reset) */
    setDefault: () => void
    /** sets the track changes sidebar state */
    setIsTrackChangesExpanded?: (isTrackChangesExpanded: boolean) => void
    /** sets the status of tree loading or not */
    setIsTreeLoading: (isLoading: boolean) => void
    /** sets the status of tree mapping/rendering in progress */
    setIsTreeMappingInProgress?: (isMappingInProgress: boolean) => void
    /** sets the project settings */
    setProjectSettings: (projectSettings: ProjectSettings) => void
    /**sets the familyAddedProductTypes */
    setFamilyAddedProductTypes?: (
        familyAddedProductTypes: ProjectElementNode[]
    ) => void
    /** sets the tree reference for virtualization support */
    setTreeRef?: (treeRef: React.MutableRefObject<ScrollableTreeRef>) => void
    /** sets the selected element data (ProjectUniformatClassification or UniformatClassification) containing uniformatClassificationProperties and other classification details */
    setSelectedElementData?: (
        selectedElementData:
            | Partial<ProjectUniformatClassification>
            | Partial<UniformatClassification>
            | undefined
    ) => void
    /** marks element as currently being processed (race condition prevention) */
    markElementAsBeingProcessed?: (elementId: string) => void
    /** marks element as recently processed with grace period (race condition prevention) */
    markElementAsRecentlyProcessed?: (elementId: string) => void
    /** checks if element is currently being processed */
    isElementBeingProcessed?: (elementId: string) => boolean
    /** checks if element was recently processed (within grace period) */
    isElementRecentlyProcessed?: (elementId: string) => boolean
    /** clears all grace period timers and resets processing state (cleanup on unmount) */
    clearAllGraceTimers?: () => void
    /** triggers BP sync check after content save (increments bpSyncTrigger counter) */
    triggerBPSyncCheck?: () => void
    /** sets content IDs that should be replaced on next fetch (e.g., user-added content replaced by BP) */
    setContentIdsToReplaceOnNextFetch?: (contentIds: string[]) => void
    /** clears content IDs to replace */
    clearContentIdsToReplaceOnNextFetch?: () => void
    /** sets whether an action button mousedown is in progress (touchpad click fix) */
    setIsActionButtonMousedown: (isMousedown: boolean) => void
    /** sets whether Best Practice automation is currently in progress */
    setIsBPAutomationInProgress?: (isInProgress: boolean) => void
}

/** Represents the state and actions for workspace 2.0 management */
export interface Workspace2Store {
    /** indicates whether the content is from OM/SMM */
    isContentTeamFromStudioPage: boolean
    /** stores the current project ID */
    projectId?: string
    /** stores the tree data pre-processed in tree data mapper */
    treeData: DataNode[]
    /** indicates whether text is currently being edited in PlainText */
    isEditingPlainText: boolean
    /** indicates whether the beta workspace is currently enabled */
    isBetaWorkspaceEnabled?: boolean
    /** indicates whether the system is currently in maintenance mode */
    isUnderMaintenance?: boolean
    /* stores the status of tree loading or not*/
    isTreeLoading: boolean
    /** indicates whether tree mapping/rendering is in progress */
    isTreeMappingInProgress?: boolean
    /** stores the most recent error that triggered maintenance mode */
    lastError?: Error | null
    /** stores the currently selected project element node from elementTree */
    selectedProjectElementNode: Partial<ProjectElementNode>
    /** stores the currently selected project element node id from elementTree */
    selectedProjectElementNodeId: string
    /** stores the current active tab index (non-zero based) */
    activeTab: number
    /** stores the keys of nodes that are currently being loaded */
    articlesLoading: string[]
    /** stores the keys of nodes that are currently expanded */
    expandedKeys: Key[]
    /** stores the selected tree content item view nodes */
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
    /** indicates whether the track changes sidebar is expanded or not */
    isTrackChangesExpanded?: boolean
    /** stores the previous tree content item view nodes */
    previousContentItemViewNodes?: Partial<ContentItemViewNodesType>[]
    /** stores the current project uniformat classification data */
    classificationNodes?: DataNode[]
    /** stores the currently being added text content element */
    textContentElementToAdd: Partial<TextContentElement> | null
    /** stores the current project settings */
    projectSettings: ProjectSettings
    /** signal to deactivate all CKEditor instances when contentItemViewNodes changes */
    editorDeactivationSignal: number
    /** stores the family added product types */
    familyAddedProductTypes?: ProjectElementNodeWithDrawingDesignation[]
    /** reference to the tree component for virtualization support */
    treeRef?: React.MutableRefObject<ScrollableTreeRef>
    /** stores the selected element data (ProjectUniformatClassification or UniformatClassification) containing uniformatClassificationProperties and other classification details for assembly nodes */
    selectedElementData?:
        | Partial<ProjectUniformatClassification>
        | Partial<UniformatClassification>
        | undefined
    /** tracks elements currently being saved (race condition prevention) */
    elementsBeingProcessed?: Set<string>
    /** tracks elements recently processed with timestamps for grace period (race condition prevention) */
    recentlyProcessedElements?: Map<string, number>
    /** counter that triggers BP sync check after content save */
    bpSyncTrigger?: number
    /** content IDs to replace on next fetch (e.g., user-added content replaced by BP sync) */
    contentIdsToReplaceOnNextFetch?: string[]
    /** indicates whether an action button mousedown is in progress (touchpad click fix) */
    isActionButtonMousedown: boolean
    /** indicates whether Best Practice automation is currently in progress */
    isBPAutomationInProgress?: boolean
    /** keys of nodes with in-flight deactivation mutations */
    deactivationPendingIds: string[]
    /** stores the actions available for the workspace */
    actions: Workspace2Actions
}

/** Represents the state and actions for workspace 2.0 management */
export interface ElementItemStoreType {
    /** stores the selected element item value */
    elementItemValue: ElementItemNode
    /** sets the selected element item value */
    setElementItemValue: (elem: ElementItemNode) => void
    /** Element format (DIVISIONS, FAMILIES, etc.) for determining isProductFamilyGroupView */
    selectedElementFormat?: string
    /** sets the selected element format */
    setSelectedElementFormat?: (format: string) => void
    /** Selected element filter value (e.g., "Added to Project") for PT cache optimization */
    selectedElementFilterValue?: string
    /** sets the selected element filter value */
    setSelectedElementFilterValue?: (filterValue: string) => void
}

export interface ProjectPropertiesStoreType {
    assignedUsersList: AssignedUserList[]
    setAssignedUsersList: (users: AssignedUserList[]) => void
}

export interface LeftMostPaneStoreType {
    /** Determines whether the leftmost pane is visible. */
    showLeftMostPane: boolean
    /** Whether the element panel state should be updated. */
    updateElementPanelState?: boolean

    /** Determines whether the search bar is visible. */
    isSearchBarDisplayed?: boolean

    /** The current active pane identifier. */
    currentPane: string

    /** Handles collapsing the left panel. */
    setshowLeftMostPane?: (value: boolean) => void

    /** Sets the current active pane. */
    setCurrentPane: (value: string) => void

    /** Updates the element panel state. */
    setUpdateElementPanelState?: (value: boolean) => void

    /** Handles show/hide the search bar */
    setIsSearchBarDisplayed?: (value: boolean) => void
}

export enum FileType {
    PDF = 'PDF',
    DOCX = 'DOCX',
}
/** Available file types for export operations */
export type ExportFileType = 'pdf' | 'docx'

/** Actions interface for ActionBar store operations */
export interface ActionBarActions {
    /** Set loading state for export operations */
    setIsExportLoading: (type: FileType, loading: boolean) => void
    /** Set loading state for supporting documents */
    setIsSupportingDocsLoading: (loading: boolean) => void
    /** Set export URL with type safety */
    setExportUrl: (type: ExportFileType, url: string) => void
    /** Set supporting documents URL */
    setSupportingDocsUrl: (url: string) => void
    /** Set selected OM version */
    setSelectedOMVersion: (
        selectedOMVersion: FamilyIteration | undefined
    ) => void
    /** Set OM dropdown options */
    setOMDropdownOptions: (omDropdownOptions: FamilyIteration[]) => void
    /** Set default element list (backward compatibility) */
    setElementList: (elementList: ProjectElementForImport[] | null) => void
    /** Set assembly panel element list */
    setAssemblyElementList: (
        elementList: ProjectElementForImport[] | null
    ) => void
    /** Set division panel element list */
    setDivisionElementList: (
        elementList: ProjectElementForImport[] | null
    ) => void
    /** Set sustainability panel element list */
    setSustainabilityElementList: (
        elementList: ProjectElementForImport[] | null
    ) => void
}

/** ActionBar store state interface with support for multiple panel instances */
export interface ActionBarStoreType {
    /** PDF export loading state */
    isPDFExportLoading: boolean
    /** Word export loading state */
    isWordExportLoading: boolean
    /** Supporting docs loading state */
    isSupportingDocsLoading: boolean
    /** PDF export URL */
    pdfExportUrl: string
    /** Word export URL */
    wordExportUrl: string
    /** Supporting docs URL */
    supportingDocsUrl: string
    /** Currently selected OM version */
    selectedOMVersion: FamilyIteration | undefined
    /** Available OM dropdown options */
    omDropdownOptions: FamilyIteration[]
    /** Default element list (for backward compatibility) */
    elementList: ProjectElementForImport[] | null
    /** Set element list for assembly panels */
    assemblyElementList: ProjectElementForImport[] | null
    /** Set element list for division panels */
    divisionElementList: ProjectElementForImport[] | null
    /** Set element list for sustainability panels */
    sustainabilityElementList: ProjectElementForImport[] | null
    /** Available actions for state management */
    actions: ActionBarActions
}

export interface ScrollableTreeRef {
    scrollTo: (options: {
        key: string | number
        align?: 'top' | 'bottom' | 'auto'
        offset?: number
    }) => void
}

export type ElementTreeView = keyof typeof ElementTreeViews
